/**
 * SignalDot — LED Communication Language Component
 *
 * The red LED is Supabank's visual language for communicating importance.
 * Use these signals consistently across all touchpoints.
 *
 * @see Living Brand Guidelines in Canon Design System
 */

import * as React from 'react';
import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

// Brand colors (SSOT: Brand Book at /design/canon)
const SIGNAL_RED = '#FF2A00';
const CRYPTO_MINT = '#10B77F';
const SUPABANK_BLUE = '#3B82F6';

// CSS Keyframes injected via style tag
const keyframesStyles = `
@keyframes signal-single-blink {
  0%, 100% { opacity: 0; }
  50% { opacity: 1; }
}

@keyframes signal-double-blink {
  0%, 89% { opacity: 0; }
  90% { opacity: 1; }
  93% { opacity: 0; }
  96% { opacity: 1; }
  100% { opacity: 0; }
}

@keyframes signal-pulse {
  0%, 100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
}

@keyframes signal-breathe {
  0%, 100% {
    opacity: 0.4;
    box-shadow: 0 0 4px currentColor;
  }
  50% {
    opacity: 1;
    box-shadow: 0 0 12px currentColor;
  }
}

@keyframes signal-urgent {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  25% {
    opacity: 0;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
  75% {
    opacity: 0;
    transform: scale(0.8);
  }
}
`;

// Inject keyframes once
if (typeof document !== 'undefined') {
  const styleId = 'signal-dot-keyframes';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = keyframesStyles;
    document.head.appendChild(style);
  }
}

const signalDotVariants = cva(
  'inline-block rounded-full flex-shrink-0',
  {
    variants: {
      /**
       * Signal type determines the animation pattern
       * - static: Always visible, no animation (active/online state)
       * - attention: Single blink - "look here"
       * - important: Double blink like logo - "new/important"
       * - pulse: Continuous gentle pulse - "live/processing"
       * - breathe: Subtle glow breathing - "ambient awareness"
       * - urgent: Fast urgent blink - "action required now"
       */
      signal: {
        static: '',
        attention: '',
        important: '',
        pulse: '',
        breathe: '',
        urgent: '',
      },
      /**
       * Size variants
       */
      size: {
        xs: 'h-1.5 w-1.5',      // 6px - inline text, badges
        sm: 'h-2 w-2',          // 8px - menu items, labels
        md: 'h-2.5 w-2.5',      // 10px - default, notifications
        lg: 'h-3 w-3',          // 12px - headers, prominent callouts
        xl: 'h-4 w-4',          // 16px - hero sections, major alerts
      },
      /**
       * Color variants
       */
      color: {
        red: '',      // Primary - attention, important
        mint: '',     // Success, transactions, positive
        blue: '',     // Info, neutral, processing
        amber: '',    // Warning, caution
      },
    },
    defaultVariants: {
      signal: 'static',
      size: 'md',
      color: 'red',
    },
  }
);

export interface SignalDotProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof signalDotVariants> {
  /**
   * Animation duration in ms (for customization)
   * Default varies by signal type
   */
  duration?: number;
  /**
   * Show glow effect around the dot
   */
  glow?: boolean;
  /**
   * Accessible label for screen readers
   */
  label?: string;
}

const getColorValue = (color: 'red' | 'mint' | 'blue' | 'amber' | null | undefined) => {
  switch (color) {
    case 'mint': return CRYPTO_MINT;
    case 'blue': return SUPABANK_BLUE;
    case 'amber': return '#F59E0B';
    case 'red':
    default: return SIGNAL_RED;
  }
};

const getDefaultDuration = (signal: string | null | undefined) => {
  switch (signal) {
    case 'attention': return 2000;    // 2s single blink
    case 'important': return 5000;    // 5s double blink (matches logo)
    case 'pulse': return 1500;        // 1.5s pulse
    case 'breathe': return 3000;      // 3s breathe
    case 'urgent': return 800;        // 0.8s urgent
    default: return 0;
  }
};

const getAnimationName = (signal: string | null | undefined) => {
  switch (signal) {
    case 'attention': return 'signal-single-blink';
    case 'important': return 'signal-double-blink';
    case 'pulse': return 'signal-pulse';
    case 'breathe': return 'signal-breathe';
    case 'urgent': return 'signal-urgent';
    default: return 'none';
  }
};

export const SignalDot = React.forwardRef<HTMLSpanElement, SignalDotProps>(
  ({
    className,
    signal,
    size,
    color,
    duration,
    glow = false,
    label,
    style,
    ...props
  }, ref) => {
    const colorValue = getColorValue(color);
    const animDuration = duration ?? getDefaultDuration(signal);
    const animName = getAnimationName(signal);

    const dotStyle: React.CSSProperties = {
      backgroundColor: colorValue,
      color: colorValue, // for currentColor in box-shadow
      animation: animName !== 'none'
        ? `${animName} ${animDuration}ms ease-in-out infinite`
        : undefined,
      boxShadow: glow ? `0 0 8px ${colorValue}` : undefined,
      ...style,
    };

    return (
      <span
        ref={ref}
        role="status"
        aria-label={label || getDefaultLabel(signal, color)}
        className={cn(signalDotVariants({ signal, size, color }), className)}
        style={dotStyle}
        {...props}
      />
    );
  }
);

SignalDot.displayName = 'SignalDot';

// Helper function for default aria labels
function getDefaultLabel(
  signal: string | null | undefined,
  color: string | null | undefined
): string {
  const signalText = signal === 'urgent' ? 'urgent'
    : signal === 'important' ? 'important'
    : signal === 'attention' ? 'new'
    : signal === 'pulse' ? 'processing'
    : 'active';

  const colorText = color === 'mint' ? 'success'
    : color === 'amber' ? 'warning'
    : color === 'blue' ? 'info'
    : '';

  return `${colorText} ${signalText} indicator`.trim();
}

// ============================================
// Compound Components for Common Use Cases
// ============================================

/**
 * New Badge - for new features, menu items
 */
export const NewBadge: React.FC<{ className?: string }> = ({ className }) => (
  <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium', className)}>
    <SignalDot signal="important" size="xs" color="red" />
    <span className="text-muted-foreground">New</span>
  </span>
);

/**
 * Live Indicator - for real-time data, streaming
 */
export const LiveIndicator: React.FC<{
  className?: string;
  label?: string;
}> = ({ className, label = 'Live' }) => (
  <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium', className)}>
    <SignalDot signal="pulse" size="sm" color="mint" glow />
    <span className="text-crypto-mint">{label}</span>
  </span>
);

/**
 * Processing Indicator - for loading states
 */
export const ProcessingIndicator: React.FC<{
  className?: string;
  label?: string;
}> = ({ className, label = 'Processing' }) => (
  <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium', className)}>
    <SignalDot signal="breathe" size="sm" color="blue" />
    <span className="text-muted-foreground">{label}</span>
  </span>
);

/**
 * Action Required - for urgent user actions
 */
export const ActionRequired: React.FC<{
  className?: string;
  label?: string;
}> = ({ className, label = 'Action required' }) => (
  <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium', className)}>
    <SignalDot signal="urgent" size="sm" color="red" glow />
    <span className="text-destructive font-semibold">{label}</span>
  </span>
);

/**
 * Online Status - for system/user status
 */
export const OnlineStatus: React.FC<{
  className?: string;
  online?: boolean;
}> = ({ className, online = true }) => (
  <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium', className)}>
    <SignalDot
      signal={online ? 'static' : 'static'}
      size="sm"
      color={online ? 'mint' : 'amber'}
    />
    <span className={online ? 'text-crypto-mint' : 'text-amber-500'}>
      {online ? 'Online' : 'Offline'}
    </span>
  </span>
);

export default SignalDot;
